/**
 * Pictures, channel links and video cards for leagues, shared by the panels
 * (entities.ts), and the places that gather them: the homepage watch strip
 * ([data-watch] in home.html), the /watch/ page and the picture cards at the
 * top of /leagues/.
 *
 * A video card is a thumbnail linking to the video on YouTube. A plain click
 * grows it into the embedded player (see playInPage); a modified click, or no
 * JavaScript, still opens YouTube. Thumbnails come from i.ytimg.com and the player from
 * www.youtube-nocookie.com, both admitted by security.csp in site.yaml.
 */
import { CHANNEL_LABELS, MEDIA, youtubeEmbed, youtubeThumb, youtubeUrl, type Channel, type Picture, type Video } from "./media";
import { LEAGUES, type League } from "./records";

type Child = Node | string | null | undefined | false;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, ...children: Child[]): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const c of children) if (c) node.append(c);
  return node;
}

const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });

function niceDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : dateFmt.format(d);
}

/**
 * The img attributes for a league picture: the 640 px copy and the full file
 * as a srcset, so a screen loads only the size it shows, and the real
 * dimensions, so the layout does not jump when the picture arrives.
 * `sizes` is how wide the picture is drawn.
 */
function pictureSources(p: Picture, sizes: string): Record<string, string> {
  return {
    src: p.src,
    srcset: `${p.src.replace(/\.jpg$/, "-640.jpg")} 640w, ${p.src} ${p.width}w`,
    sizes,
    width: String(p.width),
    height: String(p.height),
  };
}

/**
 * A league's picture with the credit its licence asks for. `eager` is for a
 * picture that is on screen as soon as it is drawn, such as the top of a panel.
 */
export function pictureFigure(p: Picture, className = "media-figure", eager = false): HTMLElement {
  const c = p.credit;
  return el(
    "figure",
    { class: className },
    el("img", { ...pictureSources(p, "(min-width: 46rem) 44rem, 100vw"), alt: p.alt, loading: eager ? "eager" : "lazy", decoding: "async" }),
    el(
      "figcaption",
      {},
      `${p.caption}. Photo: `,
      el("a", { href: c.page, rel: "noopener", target: "_blank" }, c.author),
      ", ",
      c.licenceUrl ? el("a", { href: c.licenceUrl, rel: "noopener license", target: "_blank" }, c.licence) : c.licence,
      ".",
    ),
  );
}

/** The league's official accounts, YouTube first. */
export function channelRow(channels: Partial<Record<Channel, string>> | undefined): HTMLElement | null {
  const entries = Object.entries(channels ?? {}) as [Channel, string][];
  if (!entries.length) return null;
  return el(
    "ul",
    { class: "channels", "aria-label": "Official channels" },
    ...entries.map(([kind, href]) =>
      el("li", {}, el("a", { class: `channel channel--${kind}`, href, rel: "noopener", target: "_blank", "data-track": "channel_click" }, CHANNEL_LABELS[kind])),
    ),
  );
}

export function videoCard(v: Video, league?: League): HTMLElement {
  return el(
    "a",
    { class: "video", href: youtubeUrl(v.id), rel: "noopener", target: "_blank", "data-track": "video_click", "data-video": v.id, "data-video-title": v.title },
    el(
      "span",
      { class: "video__thumb" },
      el("img", { src: youtubeThumb(v.id), alt: "", loading: "lazy", decoding: "async", width: "480", height: "360" }),
      el("span", { class: "video__play", "aria-hidden": "true" }),
    ),
    el("span", { class: "video__title" }, v.title),
    el("span", { class: "video__meta muted" }, [league?.name, niceDate(v.date), "YouTube"].filter(Boolean).join(" · ")),
  );
}

export function videoGrid(videos: Video[], league?: (v: Video) => League | undefined): HTMLElement {
  return el("div", { class: "video-grid" }, ...videos.map((v) => videoCard(v, league?.(v))));
}

/** Every video we list, newest first, with the league it belongs to. */
function allVideos(): { video: Video; league: League }[] {
  const out: { video: Video; league: League }[] = [];
  for (const league of LEAGUES) for (const video of MEDIA[league.id]?.videos ?? []) out.push({ video, league });
  return out.sort((a, b) => b.video.date.localeCompare(a.video.date));
}

/** Homepage strip: the newest video from each league, then the next newest. */
function fillStrip(host: HTMLElement): void {
  const limit = Number(host.dataset.watchLimit ?? "6");
  const all = allVideos();
  const seen = new Set<string>();
  const firsts = all.filter(({ league }) => (seen.has(league.id) ? false : (seen.add(league.id), true)));
  const picked = [...firsts, ...all.filter((x) => !firsts.includes(x))].slice(0, limit);
  const leagueOf = new Map(picked.map(({ video, league }) => [video, league]));
  host.replaceChildren(videoGrid(picked.map((x) => x.video), (v) => leagueOf.get(v)));
}

/** The /watch/ page: a section per league with its channels and videos. */
function fillWatchPage(host: HTMLElement): void {
  const leagues = LEAGUES.filter((l) => MEDIA[l.id]?.videos?.length || MEDIA[l.id]?.channels);
  host.append(
    ...leagues.map((league) => {
      const m = MEDIA[league.id];
      return el(
        "section",
        { class: "watch-league", id: league.id },
        // h3: these sit under the page's "Latest videos" heading in watch.md.
        el("h3", {}, el("a", { class: "entity-link", href: `#league/${league.id}` }, league.name)),
        channelRow(m.channels),
        m.videos?.length ? videoGrid(m.videos) : null,
      );
    }),
  );
}

/** Picture cards for the leagues we have a photo of, above the /leagues/ tables. */
function fillShowcase(host: HTMLElement): void {
  const withPictures = LEAGUES.filter((l) => MEDIA[l.id]?.picture);
  if (!withPictures.length) return;
  const grid = el(
    "div",
    { class: "league-cards" },
    ...withPictures.map((league) => {
      const p = MEDIA[league.id].picture!;
      return el(
        "a",
        { class: "league-card", href: `#league/${league.id}` },
        el("img", { ...pictureSources(p, "(min-width: 40rem) 20rem, 100vw"), alt: p.alt, loading: "lazy", decoding: "async" }),
        el("span", { class: "league-card__body" }, el("span", { class: "league-card__name" }, league.name), el("span", { class: "league-card__meta" }, `${league.format} · ${league.base}`)),
      );
    }),
  );
  host.prepend(grid);
}

/** The one video playing on the page, and the card it replaced. */
let playing: { card: HTMLAnchorElement; node: HTMLElement } | null = null;

/** Stops the playing video and puts its card back. */
function stopPlaying(): void {
  if (!playing) return;
  playing.node.replaceWith(playing.card);
  playing = null;
}

/**
 * Grows a clicked card into a player that spans its whole row; the other cards
 * reflow round it. The player is a div, because an <a> may not contain an
 * iframe. The rest of the group is queued after it, starting with the cards
 * that follow, so the next video plays when this one ends. Starting another
 * video, Close, or closing the panel it is in stops this one.
 */
function playInPage(ev: MouseEvent): void {
  if (ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
  const card = (ev.target as Element).closest<HTMLAnchorElement>("a.video[data-video]");
  if (!card) return;
  ev.preventDefault();
  stopPlaying();

  const group = Array.from(card.parentElement?.querySelectorAll<HTMLAnchorElement>(":scope > a.video[data-video]") ?? [card]);
  const at = group.indexOf(card);
  const next = [...group.slice(at + 1), ...group.slice(0, at)].map((c) => c.dataset.video!);
  const player = el("iframe", {
    src: youtubeEmbed(card.dataset.video!, next),
    title: card.dataset.videoTitle ?? "YouTube video",
    allow: "autoplay; encrypted-media; picture-in-picture; fullscreen",
    allowfullscreen: "",
    referrerpolicy: "strict-origin-when-cross-origin",
  });
  const close = el("button", { type: "button", class: "btn btn--sm btn--ghost video__close" }, "Close");
  close.addEventListener("click", stopPlaying);
  const node = el(
    "div",
    { class: "video video--playing" },
    el("span", { class: "video__thumb" }, player),
    el("span", { class: "video__caption" }, ...Array.from(card.children).filter((c) => !c.classList.contains("video__thumb")).map((c) => c.cloneNode(true)), close),
  );
  card.replaceWith(node);
  playing = { card, node };
  node.scrollIntoView({ block: "nearest", behavior: "smooth" });
}

export function initWatch(): void {
  document.addEventListener("click", playInPage);
  // A closed panel is only hidden, and an iframe in it would keep playing.
  // "close" does not bubble, so listen in the capture phase.
  document.addEventListener("close", (ev) => playing && (ev.target as Node).contains(playing.node) && stopPlaying(), true);
  for (const host of document.querySelectorAll<HTMLElement>("[data-watch-strip]")) fillStrip(host);
  for (const host of document.querySelectorAll<HTMLElement>("[data-watch]")) fillWatchPage(host);
  for (const host of document.querySelectorAll<HTMLElement>("[data-leagues]")) fillShowcase(host);
}
